// Start of Selection
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore'
import { motion } from 'framer-motion'
import { toast } from 'react-hot-toast'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { generateAgreementPDF } from '../utils/pdfutils'
import SignatureCanvas from 'react-signature-canvas'
import { ref, uploadString } from 'firebase/storage'
import { db, storage } from '../firebase'
import { useEmail } from '../contexts/EmailContext'
import ComposeEmail from '../components/ComposeEmail'
import EmailTemplateService from '../services/EmailTemplateService'
import EmailTemplateManager from '../components/EmailTemplateManager'

// Example SVG icons
const ShareIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342
       m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0
       a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684
       3 3 0 00-5.367-2.684z"
    />
  </svg>
)

const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
)

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0
       113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
)

// 1) Helper to transform near-white pixels to black on a signature dataURL
function convertWhiteToBlack(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0)

      const imageData = ctx.getImageData(0, 0, img.width, img.height)
      const d = imageData.data
      for (let i = 0; i < d.length; i += 4) {
        const r = d[i]
        const g = d[i + 1]
        const b = d[i + 2]
        // If pixel is "almost white"
        if (r > 200 && g > 200 && b > 200) {
          // set to black
          d[i] = 0
          d[i + 1] = 0
          d[i + 2] = 0
        }
      }
      ctx.putImageData(imageData, 0, 0)
      const newUrl = canvas.toDataURL('image/png')
      resolve(newUrl)
    }
    img.onerror = reject
    img.src = dataUrl
  })
}

// Rich Text Editor Component for Agreement Editing
const AgreementEditor = ({ content, onChange, placeholder, isExpanded = false }) => {
  const editorRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  // Convert stored content (newline-separated) to HTML for editing
  const convertToEditorFormat = (storedContent) => {
    if (!storedContent) return '';
    return storedContent
      .split('\n')
      .map(line => line.trim() ? `<p>${line}</p>` : '<br/>')
      .join('');
  };

  // Convert editor HTML back to newline-separated format for storage
  const convertFromEditorFormat = (editorHTML) => {
    if (!editorHTML) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editorHTML;
    
    // Extract text content while preserving line breaks
    const lines = [];
    const children = tempDiv.children;
    
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child.tagName === 'P') {
        lines.push(child.textContent || child.innerText || '');
      } else if (child.tagName === 'BR') {
        lines.push('');
      } else if (child.tagName === 'UL' || child.tagName === 'OL') {
        // Handle lists
        const listItems = child.querySelectorAll('li');
        listItems.forEach(li => {
          const prefix = child.tagName === 'UL' ? '• ' : '1. ';
          lines.push(prefix + (li.textContent || li.innerText || ''));
        });
      } else {
        // For other elements, just get text content
        lines.push(child.textContent || child.innerText || '');
      }
    }
    
    return lines.join('\n');
  };

  // Apply text formatting
  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      const editorHTML = editorRef.current.innerHTML;
      const convertedContent = convertFromEditorFormat(editorHTML);
      onChange(convertedContent);
    }
  };

  // Handle content changes
  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      // Mark as typing and reset timeout
      setIsTyping(true);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      const editorHTML = editorRef.current.innerHTML;
      const convertedContent = convertFromEditorFormat(editorHTML);
      onChange(convertedContent);
      
      // Clear typing state after a short delay
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
      }, 500);
    }
  }, [onChange]);

  // Handle Enter key to create proper paragraph breaks
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertHTML', false, '</p><p>');
    }
  };

  // Update editor content when content prop changes
  useEffect(() => {
    if (editorRef.current && !isTyping) {
      const editorHTML = convertToEditorFormat(content);
      
      // Only update if the content is actually different and the editor is not focused
      // This prevents cursor jumping during typing
      if (editorRef.current.innerHTML !== editorHTML && document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = editorHTML;
      }
    }
  }, [content, isTyping]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="border border-[#333333] rounded-2xl overflow-hidden bg-[#1A1A1A]">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-3 bg-black border-b border-[#333333]">
        {/* Text Formatting */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => applyFormat('bold')}
            className="px-3 py-1.5 text-sm font-bold bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#333333] rounded transition-colors text-white"
            title="Bold"
          >
            B
          </button>
          <button
            type="button"
            onClick={() => applyFormat('italic')}
            className="px-3 py-1.5 text-sm italic bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#333333] rounded transition-colors text-white"
            title="Italic"
          >
            I
          </button>
          <button
            type="button"
            onClick={() => applyFormat('underline')}
            className="px-3 py-1.5 text-sm underline bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#333333] rounded transition-colors text-white"
            title="Underline"
          >
            U
          </button>
        </div>

        <div className="w-px h-4 bg-[#666666]"></div>

        {/* Lists */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => applyFormat('insertUnorderedList')}
            className="px-3 py-1.5 text-sm bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#333333] rounded transition-colors text-white"
            title="Bullet List"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => applyFormat('insertOrderedList')}
            className="px-3 py-1.5 text-sm bg-[#1A1A1A] hover:bg-[#2A2A2A] border border-[#333333] rounded transition-colors text-white"
            title="Numbered List"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h2a1 1 0 011 1v2M7 4H5a2 2 0 00-2 2v6a2 2 0 002 2h8a2 2 0 002-2V6a2 2 0 00-2-2h-2M7 4v2a1 1 0 001 1h2a1 1 0 001-1V4" />
            </svg>
          </button>
        </div>

        {/* Word Count */}
        <div className="ml-auto text-xs text-[#666666]">
          {content ? content.replace(/\n/g, ' ').length : 0} characters
        </div>
      </div>

      {/* Editor Content */}
      <div 
        ref={editorRef}
        contentEditable
        onInput={handleContentChange}
        onKeyDown={handleKeyDown}
        className="p-4 bg-[#1A1A1A] text-white focus:outline-none prose prose-sm prose-invert max-w-none"
        style={{
          minHeight: isExpanded ? '500px' : '300px',
          lineHeight: '1.6'
        }}
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />
      
      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #666666;
          font-style: italic;
          pointer-events: none;
        }
        [contenteditable]:empty:focus:before {
          content: '';
        }
        /* Match Agreement.js display formatting */
        .prose p { 
          margin: 0 0 1.5rem 0; /* mb-6 equivalent */
          line-height: 1.6;
        }
        .prose ul, .prose ol { 
          margin: 0 0 1.5rem 0; 
          padding-left: 1.5rem; 
        }
        .prose li { 
          margin: 0.25rem 0; 
        }
        .prose strong { 
          font-weight: 600; 
        }
        .prose em { 
          font-style: italic; 
        }
        [contenteditable] {
          color: #fff;
        }
        [contenteditable] * {
          color: inherit;
        }
        /* Ensure proper paragraph spacing matches display */
        [contenteditable] p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
};

export default function Agreement() {
  const { agreementId } = useParams()
  const navigate = useNavigate()
  const auth = getAuth()
  const location = useLocation()
  const { openCompose, activeAccountId, accounts } = useEmail() || {}

  const [agreement, setAgreement] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [isPublicView, setIsPublicView] = useState(false)
  const [userProfile, setUserProfile] = useState(null)

  // Email composer state
  const [showEmailComposer, setShowEmailComposer] = useState(false)
  const [showSentSuccess, setShowSentSuccess] = useState(false)
  const [sentToEmail, setSentToEmail] = useState('')

  // Editing states
  const [isEditing, setIsEditing] = useState(false)
  const [editedText, setEditedText] = useState('')
  const [aiInstruction, setAiInstruction] = useState('')
  const [isAiLoading, setIsAiLoading] = useState(false)
  const [showExpandedEditor, setShowExpandedEditor] = useState(false)

  // Signature states
  const [showSignatureCanvas, setShowSignatureCanvas] = useState(false)
  const [signatureType, setSignatureType] = useState('draw') // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState('')
  const signatureRef = useRef(null)

  // For scrolling to signature area
  const signatureSectionRef = useRef(null)
  const shouldScrollToSignature = useRef(false)

  // Scroll to signature section when it becomes visible
  useEffect(() => {
    if (showSignatureCanvas && shouldScrollToSignature.current && signatureSectionRef.current) {
      // Use a small delay to ensure the element is fully rendered
      const timer = setTimeout(() => {
        if (signatureSectionRef.current) {
          signatureSectionRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start', // Scroll to top of element
            inline: 'nearest'
          })
          shouldScrollToSignature.current = false // Reset flag
        }
      }, 150)
      
      return () => clearTimeout(timer)
    }
  }, [showSignatureCanvas])

  // Email template state
  const [emailTemplates, setEmailTemplates] = useState([])
  const [selectedEmailStyle, setSelectedEmailStyle] = useState('professional')
  const [showTemplateManager, setShowTemplateManager] = useState(false)
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)

  // Initialize email template service
  const emailTemplateService = useMemo(() => {
    return auth.currentUser ? EmailTemplateService : null
  }, [auth.currentUser])

  // Load custom email templates
  useEffect(() => {
    const loadTemplates = async () => {
      if (!emailTemplateService) return
      
      setIsLoadingTemplates(true)
      try {
        const templates = await emailTemplateService.getTemplates()
        setEmailTemplates(templates)
      } catch (error) {
        console.error('Error loading email templates:', error)
      } finally {
        setIsLoadingTemplates(false)
      }
    }

    loadTemplates()
  }, [emailTemplateService])

  // Fetch user profile for better email signatures
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!auth.currentUser) return;
      
      try {
        const db = getFirestore();
        const profileRef = doc(db, 'profiles', auth.currentUser.uid);
        const profileDoc = await getDoc(profileRef);
        
        if (profileDoc.exists()) {
          setUserProfile(profileDoc.data());
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserProfile();
      } else {
        setUserProfile(null);
      }
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    let isMounted = true;
    
    const fetchAgreement = async () => {
      try {
        const db = getFirestore()
        const docRef = doc(db, 'agreements', agreementId)
        const agreementDoc = await getDoc(docRef)
        if (!agreementDoc.exists()) {
          if (isMounted) {
            toast.error('Agreement not found')
            // Don't redirect public users to /app/agreements as they can't access it
            if (auth.currentUser) {
              navigate('/app/agreements')
            } else {
              navigate('/')
            }
          }
          return
        }
        const data = agreementDoc.data()
        
        // Safely handle date conversion
        if (data.createdAt) {
          try {
            // Check if it's a Firestore Timestamp
            if (data.createdAt.toDate) {
              data.createdAt = data.createdAt.toDate()
            } else if (typeof data.createdAt === 'string') {
              data.createdAt = new Date(data.createdAt)
            }
          } catch (err) {
            console.error('Error parsing date:', err)
            data.createdAt = null
          }
        }
        
        if (isMounted) {
          setAgreement({ id: agreementDoc.id, ...data })
          setEditedText(data.text || '')
        }
      } catch (error) {
        console.error('Error fetching agreement:', error)
        if (isMounted) {
          toast.error('Failed to load agreement')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    // Add authentication state listener to ensure we have the latest auth state
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (isMounted) {
        setIsPublicView(!user)
        // Only try to fetch agreement if we have a valid ID
        if (agreementId) {
          fetchAgreement()
        } else if (isMounted) {
          setLoading(false)
          toast.error('Invalid agreement ID')
          navigate('/')
        }
      }
    }, (error) => {
      console.error("Auth state change error:", error);
      if (isMounted) {
        setLoading(false);
        toast.error('Authentication error');
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    }
  }, [agreementId, navigate]);

  // Download PDF function - ALWAYS fetch fresh data from Firestore
  const handleDownloadPDF = useCallback(async (agreementData = null) => {
    setDownloading(true);
    
    try {
      // ALWAYS fetch fresh data from Firestore to ensure we have the latest
      const db = getFirestore()
      const docRef = doc(db, 'agreements', agreementId)
      const agreementDoc = await getDoc(docRef)
      
      if (!agreementDoc.exists()) {
        toast.error('Agreement not found');
        setDownloading(false);
        return;
      }
      
      // Get fresh data from Firestore
      let data = { id: agreementDoc.id, ...agreementDoc.data() }
      
      // Handle date conversion
      if (data.createdAt) {
        try {
          if (data.createdAt.toDate && typeof data.createdAt.toDate === 'function') {
            data.createdAt = data.createdAt.toDate()
          } else if (data.createdAt.seconds) {
            data.createdAt = new Date(data.createdAt.seconds * 1000)
          }
        } catch (err) {
          console.warn('Error parsing createdAt:', err)
        }
      }
      
      // Merge with any provided data (for signatures that were just added)
      if (agreementData) {
        data = { ...data, ...agreementData }
      }
      
      // Validate we have the essential data
      if (!data.id) {
        toast.error('Invalid agreement data');
        setDownloading(false);
        return;
      }
      
      // Debug: Log what fields we have
      console.log('PDF Generation - Agreement fields:', {
        id: data.id,
        title: data.title,
        hasText: !!data.text,
        textLength: data.text?.length || 0,
        hasContent: !!data.content,
        contentLength: data.content?.length || 0,
        hasClientSignature: !!data.clientSignature,
        hasFreelancerSignature: !!data.freelancerSignature
      })
      
      // Pass the full signature objects (with signature and timestamp)
      const pdfDoc = generateAgreementPDF(data, {
        freelancerSignature: data.freelancerSignature || null,
        clientSignature: data.clientSignature || null,
      });
      
      // Generate filename
      const safeTitle = (data.title || 'agreement').replace(/[^a-z0-9]/gi, '_').toLowerCase()
      const filename = `${safeTitle}_${data.id || 'draft'}.pdf`;
      pdfDoc.save(filename);
      toast.success('Agreement downloaded successfully');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(`Failed to download: ${error.message || 'Unknown error'}`);
    } finally {
      setDownloading(false);
    }
  }, [agreementId]);

  // If ?download in URL, auto download
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has('download') && agreement) {
      handleDownloadPDF();
    }
  }, [location.search, handleDownloadPDF, agreement]);

  // Copy link
  const handleCopyLink = () => {
    try {
      const link = `${window.location.origin}/agreement/${agreementId}`
      navigator.clipboard.writeText(link).then(() => {
        toast.success('Link copied to clipboard')
      }).catch(err => {
        console.error('Error copying to clipboard:', err)
        toast.error('Failed to copy link')
      })
    } catch (error) {
      console.error('Error in copy link function:', error)
      toast.error('Failed to copy link')
    }
  }

  // Start editing
  const handleEditClick = () => {
    setIsEditing(true)
  }

  // Save agreement text
  const handleSave = async () => {
    if (!agreement) return
    setLoading(true)
    try {
      const db = getFirestore()
      await updateDoc(doc(db, 'agreements', agreement.id), {
        text: editedText,
      })
      setAgreement({ ...agreement, text: editedText })
      toast.success('Agreement updated successfully')
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving edits:', error)
      toast.error('Failed to save agreement')
    } finally {
      setLoading(false)
    }
  }

  // AI Edit
  const handleAiEdit = async () => {
    if (!editedText.trim()) {
      toast.error('No agreement text to edit')
      return
    }
    if (!aiInstruction.trim()) {
      toast.error('Please enter an instruction for AI editing')
      return
    }

    setIsAiLoading(true)
    try {
      // editedText is already in newline format, so use it directly
      const response = await fetch('https://klorah-fast-server-my8l.onrender.com/api/ai-edit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: editedText,
          instruction: aiInstruction,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed AI edit request')
      }

      const result = await response.json()
      if (result.edited_text) {
        // AI returns plain text, store it directly as newline-separated content
        setEditedText(result.edited_text)
        setAiInstruction('') // Clear the instruction after successful edit
        toast.success('AI edit applied successfully!')
      } else {
        toast.error('No edited text returned by AI')
      }
    } catch (err) {
      console.error(err)
      toast.error('AI edit failed. Please try again.')
    } finally {
      setIsAiLoading(false)
    }
  }

  // Cancel editing
  const handleCancel = () => {
    setEditedText(agreement?.text || '')
    setIsEditing(false)
  }

  // Clears the signature pad or typed signature
  const handleClear = () => {
    if (signatureType === 'draw') {
      signatureRef.current?.clear()
    } else {
      setTypedSignature('')
    }
  }

  // 2) final sign & save
  const handleSaveSignature = async () => {
    if (!agreement) return

    try {
      let signatureData
      if (signatureType === 'draw') {
        // if the user hasn't drawn anything
        if (!signatureRef.current || signatureRef.current?.isEmpty()) {
          toast.error('Please provide a signature')
          return
        }
        try {
          const rawDataUrl = signatureRef.current.toDataURL()
          // Convert near-white → black
          signatureData = await convertWhiteToBlack(rawDataUrl)
        } catch (err) {
          console.error('Error processing signature:', err)
          toast.error('Unable to process signature. Please try again.')
          return
        }
      } else {
        // typed signature => generate a black text signature in a canvas
        if (!typedSignature || !typedSignature.trim()) {
          toast.error('Please provide a signature')
          return
        }
        
        try {
          // For typed mode, let's do a small canvas approach to create black text on transparent
          const canvas = document.createElement('canvas')
          canvas.width = 500
          canvas.height = 150
          const ctx = canvas.getContext('2d')
          ctx.fillStyle = '#000000' // black text
          ctx.font = '48px serif'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2)
          signatureData = canvas.toDataURL('image/png')
        } catch (err) {
          console.error('Error creating typed signature:', err)
          toast.error('Unable to create signature. Please try again.')
          return
        }
      }

      // 3) Upload signatureData to Storage (only if authenticated - storage requires auth)
      if (auth.currentUser) {
        try {
          const fileRef = ref(storage, `signed_agreements/${agreementId}-${Date.now()}.png`)
          await uploadString(fileRef, signatureData, 'data_url')
        } catch (error) {
          console.error('Error uploading signature to storage:', error)
          // Don't fail the entire operation if storage upload fails
        }
      }

      // 4) Determine which signature field to update
      const isClientSigning = !auth.currentUser || (auth.currentUser && agreement.userId !== auth.currentUser.uid)
      const fieldName = isClientSigning ? 'clientSignature' : 'freelancerSignature'
      const timestamp = Date.now()
      
      // Create the signature object
      const signatureObj = {
        signature: signatureData,
        timestamp: timestamp
      }
      
      try {
        const docRef = doc(db, 'agreements', agreement.id)
        await updateDoc(docRef, {
          status: 'signed',
          signedAt: new Date().toISOString(),
          [fieldName]: signatureObj
        })

        // Update local state immediately with the new signature
        const updatedAgreement = {
          ...agreement,
          status: 'signed',
          signedAt: new Date().toISOString(),
          [fieldName]: signatureObj
        }
        setAgreement(updatedAgreement)

        toast.success('Agreement signed successfully!')
        setShowSignatureCanvas(false)
        
        // Wait a moment for Firestore to update, then download PDF
        // The download function will fetch fresh data, but we pass the signature as a fallback
        setTimeout(() => {
          try {
            handleDownloadPDF({ [fieldName]: signatureObj })
          } catch (pdfError) {
            console.error('Error downloading PDF:', pdfError)
            // Don't fail the whole operation if PDF download fails
            toast.error('Signature saved but PDF download failed. Try downloading again.')
          }
        }, 500) // Small delay to ensure Firestore has updated
        
        // Send confirmation email to agreement owner if client is signing
        if (isClientSigning && agreement.userId) {
          sendSignatureConfirmationEmail(updatedAgreement)
        }
      } catch (error) {
        console.error('Error updating agreement in Firestore:', error)
        console.error('Error code:', error.code)
        console.error('Error message:', error.message)
        console.error('Agreement ID:', agreement.id)
        console.error('Is authenticated:', !!auth.currentUser)
        console.error('Field being updated:', fieldName)
        
        // Provide more helpful error message
        if (error.code === 'permission-denied') {
          console.error('Permission denied - Firestore rules may need to be updated')
          toast.error('Permission denied. The agreement owner needs to update Firestore security rules to allow client signatures.')
        } else {
          toast.error(`Failed to save signature: ${error.message || 'Unknown error'}`)
        }
      }
    } catch (error) {
      console.error('Error in signature process:', error)
      toast.error('An unexpected error occurred. Please try again.')
    }
  }

  // Send confirmation email to agreement owner when client signs
  const sendSignatureConfirmationEmail = async (signedAgreement) => {
    try {
      // Get the agreement owner's email
      const ownerDb = getFirestore()
      const ownerProfileRef = doc(ownerDb, 'profiles', signedAgreement.userId)
      const ownerProfileDoc = await getDoc(ownerProfileRef)
      
      let ownerEmail = null
      if (ownerProfileDoc.exists()) {
        ownerEmail = ownerProfileDoc.data()?.email
      }
      
      // If no email in profile, try to get from user doc
      if (!ownerEmail) {
        const userRef = doc(ownerDb, 'users', signedAgreement.userId)
        const userDoc = await getDoc(userRef)
        if (userDoc.exists()) {
          ownerEmail = userDoc.data()?.email
        }
      }
      
      if (!ownerEmail) {
        console.log('Could not find owner email for notification')
        return
      }
      
      const clientName = signedAgreement.client || signedAgreement.clientName || 'Your client'
      const agreementTitle = signedAgreement.title || 'Service Agreement'
      const signedDate = new Date().toLocaleDateString()
      const agreementUrl = `${window.location.origin}/agreement/${signedAgreement.id}`
      
      // Import and use EmailService to send notification
      const { default: EmailService } = await import('../services/EmailService')
      
      await EmailService.sendEmail({
        user_id: signedAgreement.userId,
        to: ownerEmail,
        subject: `✅ Agreement Signed - ${agreementTitle}`,
        body: `
          <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto;">
            <h2 style="color: #1a1a1a;">Great news! Your agreement has been signed</h2>
            
            <p style="color: #666; font-size: 16px;">
              <strong>${clientName}</strong> has signed your agreement.
            </p>
            
            <div style="background: #f5f5f5; padding: 20px; border-radius: 12px; margin: 20px 0;">
              <p style="margin: 0 0 10px 0; color: #333;"><strong>Agreement:</strong> ${agreementTitle}</p>
              <p style="margin: 0 0 10px 0; color: #333;"><strong>Client:</strong> ${clientName}</p>
              <p style="margin: 0; color: #333;"><strong>Signed on:</strong> ${signedDate}</p>
            </div>
            
            <p style="color: #666; font-size: 16px;">
              You can view the signed agreement and download a PDF copy using the link below.
            </p>
            
            <a href="${agreementUrl}" style="display: inline-block; background-color: #FFB27D; color: black; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 15px 0; font-weight: 500;">
              View Signed Agreement
            </a>
            
            <p style="color: #999; font-size: 14px; margin-top: 30px;">
              This is an automated notification from Klorah.
            </p>
          </div>
        `,
        account_id: ''
      })
      
      console.log('Signature confirmation email sent to agreement owner')
    } catch (error) {
      console.error('Error sending signature confirmation email:', error)
      // Don't show error to user - this is a background notification
    }
  }

  // Helper to open signature pad
  const openSignaturePad = () => {
    shouldScrollToSignature.current = true // Set flag to trigger scroll
    setShowSignatureCanvas(true)
    // The useEffect will handle the scrolling when the component renders
  }

  // Get user name for email signatures
  const getUserName = () => {
    if (auth.currentUser?.displayName) return auth.currentUser.displayName
    if (userProfile?.name) return userProfile.name
    if (userProfile?.businessName) return userProfile.businessName
    if (auth.currentUser?.email) {
      return auth.currentUser.email.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
    return 'Your photographer'
  }

  // Handle email style change
  const handleEmailStyleChange = async (style) => {
    setSelectedEmailStyle(style)
    
    // Update usage count for custom templates
    if (style.startsWith('custom-') && emailTemplateService) {
      try {
        const templateId = style.replace('custom-', '')
        await emailTemplateService.updateUsage(templateId)
        // Refresh templates to update usage count
        const updatedTemplates = await emailTemplateService.getTemplates()
        setEmailTemplates(updatedTemplates)
      } catch (error) {
        console.error('Error updating template usage:', error)
      }
    }
  }

  // Generate email content based on selected style
  const generateEmailContent = (style = selectedEmailStyle) => {
    const agreementUrl = `${window.location.origin}/agreement/${agreementId}`
    const userName = getUserName()
    
    // Agreement variables for template replacement
    const agreementVars = {
      clientName: agreement.client || agreement.clientName || 'Client',
      firstName: (agreement.client || agreement.clientName || 'Client').split(' ')[0],
      agreementTitle: agreement.title || 'Service Agreement',
      agreementUrl: agreementUrl,
      amount: agreement.price ? `${agreement.currency || 'USD'} ${agreement.price}` : '',
      dueDate: agreement.dueDate ? new Date(agreement.dueDate).toLocaleDateString() : '',
      userName: userName,
      businessName: userProfile?.businessName || userName,
      currentDate: new Date().toLocaleDateString()
    }

    // Check if custom template is selected
    if (style.startsWith('custom-')) {
      const templateId = style.replace('custom-', '')
      const template = emailTemplates.find(t => t.id === templateId)
      
      if (template && emailTemplateService) {
        return emailTemplateService.replaceVariables(template.content, agreementVars)
      }
    }

    // Built-in templates
    const templates = {
      professional: `
        <p>Dear ${agreementVars.clientName},</p>
        
        <p>Thank you for choosing our photography services. I have prepared a service agreement for our upcoming project.</p>
        
        <p><strong>Agreement Details:</strong><br/>
        ${agreementVars.agreementTitle}<br/>
        ${agreementVars.amount ? `Amount: ${agreementVars.amount}<br/>` : ''}
        ${agreementVars.dueDate ? `Timeline: ${agreementVars.dueDate}<br/>` : ''}</p>
        
        <p>Please review and sign the agreement using the link below:</p>
        
        <p><a href="${agreementVars.agreementUrl}" style="display: inline-block; background-color: #FFB27D; color: black; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 15px 0; font-weight: 500;">Review & Sign Agreement</a></p>
        
        <p>Should you have any questions or require clarification on any aspect of the agreement, please don't hesitate to contact me.</p>
        
        <p>I look forward to working with you.</p>
        
        <p>Best regards,<br/>
        ${agreementVars.userName}</p>
      `,
      friendly: `
        <p>Hi ${agreementVars.firstName}!</p>
        
        <p>I hope this email finds you well. I've prepared a service agreement for our project and I'm excited to work with you!</p>
        
        <p><strong>Here are the details:</strong><br/>
        ${agreementVars.agreementTitle}<br/>
        ${agreementVars.amount ? `Amount: ${agreementVars.amount}<br/>` : ''}
        ${agreementVars.dueDate ? `Timeline: ${agreementVars.dueDate}<br/>` : ''}</p>
        
        <p>You can review and sign the agreement here:</p>
        
        <p><a href="${agreementVars.agreementUrl}" style="display: inline-block; background-color: #FFB27D; color: black; padding: 12px 24px; text-decoration: none; border-radius: 8px; margin: 15px 0; font-weight: 500;">Review & Sign Agreement</a></p>
        
        <p>If you have any questions or want to discuss anything, just let me know!</p>
        
        <p>Looking forward to our collaboration!</p>
        
        <p>Best,<br/>
        ${agreementVars.userName}</p>
      `
    }

    return templates[style] || templates.professional
  }

  // Handle sending agreement to client
  const handleSendToClient = () => {
    if (!agreement) {
      toast.error('Agreement data not available')
      return
    }

    // Check if we have client email stored in the agreement
    const clientEmail = agreement.clientEmail
    if (!clientEmail) {
      toast.error('No client email found. Please edit the agreement to add client email.')
      return
    }

    // Check if user has an email account connected
    if (!activeAccountId && (!accounts || accounts.length === 0)) {
      toast.error('Please connect an email account first. Go to Settings > Email Accounts.')
      return
    }

    // Open the template selection modal first
    setShowEmailComposer({
      to: clientEmail,
      subject: `Service Agreement - ${agreement.title || 'Please Review & Sign'}`
      // Note: content will be added after template selection
    })
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FFB27D]" />
      </div>
    )
  }

  if (!agreement) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#1A1A1A] rounded-lg p-8 text-center">
          <h1 className="text-2xl font-medium text-[#FFB27D] mb-4">Agreement Not Found</h1>
          <p className="text-[#999999] mb-6">
            This agreement may have been deleted or you might not have permission to view it.
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate(-1)}
              className="w-full py-2 px-4 bg-[#FFB27D] text-black rounded-lg hover:bg-[#FFA164] transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full py-2 px-4 bg-[#1A1A1A] text-[#FFB27D] border border-[#FFB27D] rounded-lg hover:bg-[#FFB27D] hover:text-black transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  try {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Header + Actions */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-6">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl sm:text-3xl font-semibold text-white">
                    {agreement.title || (
                      (agreement.client || agreement.clientName) 
                        ? `Agreement with ${agreement.client || agreement.clientName}` 
                        : 'Agreement'
                    )}
                  </h1>
                  {/* Status Badge */}
                  {agreement.status === 'sent' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Sent
                    </span>
                  )}
                  {agreement.status === 'signed' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Signed
                    </span>
                  )}
                  {agreement.status === 'draft' && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[#333333] text-[#999999] border border-[#444444]">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Draft
                    </span>
                  )}
                </div>
                <p className="mt-2 text-[#999999]">
                  {agreement.createdAt 
                    ? (typeof agreement.createdAt.toLocaleDateString === 'function' 
                      ? agreement.createdAt.toLocaleDateString() 
                      : new Date().toLocaleDateString())
                    : new Date().toLocaleDateString()}
                  {agreement.sentAt && (
                    <span className="ml-2 text-[#666666]">
                      • Sent {new Date(agreement.sentAt).toLocaleDateString()}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                {!isPublicView && (
                  <>
                    <button
                      onClick={handleCopyLink}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5
                        border border-[#333333] text-sm font-medium rounded-lg text-[#999999]
                        bg-[#1A1A1A] hover:bg-[#222222] hover:text-white transition-colors"
                    >
                      <ShareIcon />
                      <span>Share</span>
                    </button>
                    <button
                      onClick={handleDownloadPDF}
                      disabled={downloading}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5
                        border border-[#333333] text-sm font-medium rounded-lg text-[#999999]
                        bg-[#1A1A1A] hover:bg-[#222222] hover:text-white transition-colors"
                    >
                      <DownloadIcon />
                      <span>{downloading ? 'Downloading...' : 'Download'}</span>
                    </button>
                    {/* Send to Client Button - Prominent placement */}
                    {agreement?.clientEmail && agreement.status !== 'signed' && (
                      <button
                        onClick={handleSendToClient}
                        className={`inline-flex items-center justify-center gap-2 px-4 py-2.5
                          text-sm font-medium rounded-lg transition-colors shadow-sm ${
                            agreement.status === 'sent'
                              ? 'text-[#FFB27D] bg-[#FFB27D]/10 border border-[#FFB27D]/30 hover:bg-[#FFB27D]/20'
                              : 'text-black bg-[#FFB27D] hover:bg-[#FFA164]'
                          }`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span>{agreement.status === 'sent' ? 'Resend' : 'Send to Client'}</span>
                      </button>
                    )}
                  </>
                )}
                {agreement.status !== 'signed' && (
                  <>
                    <button
                      onClick={openSignaturePad}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5
                        text-sm font-medium rounded-lg text-black bg-[#FFB27D]
                        hover:bg-[#FFA164] transition-colors"
                    >
                      <EditIcon />
                      <span>Sign Now</span>
                    </button>
                    {!isEditing && (
                      <button
                        onClick={handleEditClick}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5
                          text-sm font-medium rounded-lg text-black bg-[#FFB27D]
                          hover:bg-[#ffa864] transition-colors"
                      >
                        <EditIcon />
                        <span>Edit Agreement</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Agreement Text */}
            <div className="flex flex-col lg:flex-row gap-8">
              {/* The text area */}
              <div className={`prose prose-invert ${showSignatureCanvas ? 'lg:w-3/5' : 'w-full'}`}>
                <h2 className="text-xl font-semibold text-white mb-6">Agreement Details</h2>
                <div
                  className="text-[#E6E6E6] leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: (agreement.text || '')
                      .split('\n')
                      .map(line => (line.trim() ? `<p class="mb-6">${line}</p>` : '<br/>'))
                      .join('')
                  }}
                />
              </div>

              {/* Edit Modal */}
              {isEditing && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[100]">
                  <motion.div
                    className="bg-black rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col border border-[#333333]"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                  >
                    {/* Edit Modal Header */}
                    <div className="px-8 py-6 border-b border-[#333333] bg-black rounded-t-3xl flex-shrink-0">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-xl font-medium text-white">Edit Agreement</h2>
                          <p className="text-sm text-[#666666] mt-1">Make changes to your agreement with AI assistance</p>
                        </div>
                        <button 
                          onClick={handleCancel}
                          className="p-2 hover:bg-[#1A1A1A] rounded-full transition-colors text-[#666666] hover:text-white"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Edit Modal Body */}
                    <div className="px-8 py-6 bg-black overflow-y-auto flex-1 min-h-0 space-y-6">
                      {/* Agreement Editor */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-white">Agreement Text</label>
                        <AgreementEditor
                          content={editedText}
                          onChange={(newContent) => setEditedText(newContent)}
                          placeholder="Enter agreement text..."
                          isExpanded={true}
                        />
                      </div>

                      {/* AI Edit Section */}
                      <div className="space-y-3">
                        <label className="block text-sm font-medium text-white">AI Assistant</label>
                        <div className="bg-[#1A1A1A] border border-[#333333] rounded-2xl p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 bg-[#FFB27D] rounded-full flex items-center justify-center flex-shrink-0">
                              <svg className="w-4 h-4 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-white mb-2">
                                Ask AI to modify your agreement
                              </h4>
                              <input
                                type="text"
                                className="w-full bg-black text-white p-3 border border-[#333333] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/20 focus:border-[#FFB27D] placeholder-[#666666]"
                                placeholder="e.g., 'Make it more formal', 'Add a cancellation clause', 'Simplify the language'"
                                value={aiInstruction}
                                onChange={(e) => setAiInstruction(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !isAiLoading && aiInstruction.trim()) {
                                    handleAiEdit();
                                  }
                                }}
                              />
                              <div className="flex items-center justify-between mt-3">
                                <p className="text-xs text-[#666666]">
                                  Press Enter to apply AI changes
                                </p>
                                <button
                                  onClick={handleAiEdit}
                                  disabled={isAiLoading || !aiInstruction.trim()}
                                  className="px-4 py-2 bg-[#FFB27D] hover:bg-[#FFA164] text-black text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {isAiLoading ? (
                                    <div className="flex items-center gap-2">
                                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                                      <span>Applying...</span>
                                    </div>
                                  ) : (
                                    'Apply AI Edit'
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Edit Modal Footer */}
                    <div className="px-8 py-6 border-t border-[#333333] flex justify-end gap-4 bg-black rounded-b-3xl flex-shrink-0">
                      <button
                        onClick={handleCancel}
                        disabled={loading}
                        className="px-6 py-3 text-sm font-medium bg-[#1A1A1A] hover:bg-[#2A2A2A] rounded-2xl transition-colors text-[#666666] hover:text-white border border-[#333333]"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={loading}
                        className="px-6 py-3 text-sm font-medium rounded-2xl transition-colors bg-[#FFB27D] hover:bg-[#FFA164] text-black"
                      >
                        {loading ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                            <span>Saving...</span>
                          </div>
                        ) : (
                          'Save Changes'
                        )}
                      </button>
                    </div>
                  </motion.div>
                </div>
              )}

              {/* The signature area (visible if showSignatureCanvas = true) */}
              {showSignatureCanvas && (
                <div
                  ref={signatureSectionRef}
                  className="lg:w-2/5 bg-[#1A1A1A] border border-[#333333] p-6 rounded-lg shadow-md"
                >
                  <h2 className="text-xl font-semibold mb-6 text-white">Sign Agreement</h2>
                  <div className="flex gap-4 mb-4">
                    <button
                      className={`px-4 py-2 rounded-md transition-colors ${
                        signatureType === 'draw'
                          ? 'bg-[#FFB27D] text-black font-medium'
                          : 'bg-[#2A2A2A] text-[#E6E6E6] hover:bg-[#333]'
                      }`}
                      onClick={() => setSignatureType('draw')}
                    >
                      Draw Signature
                    </button>
                    <button
                      className={`px-4 py-2 rounded-md transition-colors ${
                        signatureType === 'type'
                          ? 'bg-[#FFB27D] text-black font-medium'
                          : 'bg-[#2A2A2A] text-[#E6E6E6] hover:bg-[#333]'
                      }`}
                      onClick={() => setSignatureType('type')}
                    >
                      Type Signature
                    </button>
                  </div>

                  {signatureType === 'draw' ? (
                    <div className="border border-[#444] rounded-md p-2 bg-black">
                      <SignatureCanvas
                        ref={signatureRef}
                        canvasProps={{
                          className: 'signature-canvas w-full h-48 rounded-md',
                          style: { background: '#000000' },
                        }}
                        penColor="#FFFFFF" // White pen, so user sees it on dark background
                      />
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={typedSignature}
                      onChange={(e) => setTypedSignature(e.target.value)}
                      placeholder="Type your signature here"
                      className="w-full p-3 border border-[#444] rounded-md
                        bg-black text-white focus:outline-none
                        focus:ring-2 focus:ring-[#FFB27D] focus:border-transparent"
                    />
                  )}

                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => setShowSignatureCanvas(false)}
                      className="px-4 py-2 border border-[#444] rounded-md
                        text-[#E6E6E6] bg-transparent hover:bg-[#2A2A2A] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleClear}
                      className="px-4 py-2 border border-[#444] rounded-md
                        text-[#E6E6E6] bg-[#2A2A2A] hover:bg-[#333] transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={handleSaveSignature}
                      className="px-4 py-2 rounded-md text-black bg-[#FFB27D]
                        hover:bg-[#FFA164] transition-colors ml-auto"
                    >
                      Sign & Save
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Signatures display */}
            <div className="pt-8 border-t border-[#333333]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {/* Client Signature */}
                <div>
                  <p className="font-medium text-white mb-3">Client Signature</p>
                  <div className="h-24 border-b-2 border-[#333333]">
                    {agreement.clientSignature?.signature && (
                      <img
                        src={agreement.clientSignature.signature}
                        alt="Client Signature"
                        className="h-full object-contain"
                      />
                    )}
                  </div>
                  <p className="text-sm text-[#999999] mt-2">
                    {agreement.clientSignature?.timestamp
                      ? new Date(agreement.clientSignature.timestamp).toLocaleDateString()
                      : 'Date: _____________'}
                  </p>
                </div>

                {/* Freelancer Signature */}
                <div>
                  <p className="font-medium text-white mb-3">Freelancer Signature</p>
                  <div className="h-24 border-b-2 border-[#333333]">
                    {agreement.freelancerSignature?.signature && (
                      <img
                        src={agreement.freelancerSignature.signature}
                        alt="Freelancer Signature"
                        className="h-full object-contain"
                      />
                    )}
                  </div>
                  <p className="text-sm text-[#999999] mt-2">
                    {agreement.freelancerSignature?.timestamp
                      ? new Date(agreement.freelancerSignature.timestamp).toLocaleDateString()
                      : 'Date: _____________'}
                  </p>
                </div>
              </div>
            </div>

            {/* If fully signed */}
            {agreement.status === 'signed' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 p-6 bg-[#1A1A1A] rounded-xl border border-[#333333]"
              >
                <div className="flex items-center gap-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#FFB27D]" />
                  <div>
                    <h3 className="text-lg font-semibold text-white">Agreement Signed</h3>
                    <p className="text-[#999999]">All parties have signed this agreement.</p>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  {agreement.freelancerSignature && (
                    <div className="flex justify-between text-[#999999]">
                      <span>Freelancer Signature</span>
                      <span>{agreement.freelancerSignature.timestamp ? new Date(agreement.freelancerSignature.timestamp).toLocaleDateString() : 'Unknown date'}</span>
                    </div>
                  )}
                  {agreement.clientSignature && (
                    <div className="flex justify-between text-[#999999]">
                      <span>Client Signature</span>
                      <span>{agreement.clientSignature.timestamp ? new Date(agreement.clientSignature.timestamp).toLocaleDateString() : 'Unknown date'}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Footer / Branding */}
        <div className="max-w-4xl mx-auto px-4 pb-8">
          <div className="text-center text-sm text-[#666666]">
            <a
              href="https://klorah.app"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#999999] transition-colors"
            >
              Powered by Klorah
            </a>
          </div>
        </div>

        {/* Email Template Selection Modal */}
        {showEmailComposer && typeof showEmailComposer === 'object' && !showEmailComposer.content && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#1A1A1A] rounded-xl shadow-2xl w-full max-w-md border border-[#333333]"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white">Choose Email Style</h3>
                  <button
                    onClick={() => setShowEmailComposer(false)}
                    className="text-[#666666] hover:text-white transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-3">
                  {/* Professional Template */}
                  <button
                    onClick={async () => {
                      setSelectedEmailStyle('professional')
                      await handleEmailStyleChange('professional')
                      setShowEmailComposer({
                        ...showEmailComposer,
                        content: generateEmailContent('professional')
                      })
                    }}
                    className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                      selectedEmailStyle === 'professional'
                        ? 'border-[#FFB27D] bg-[#FFB27D]/10'
                        : 'border-[#333333] hover:border-[#555555]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-white">Professional</h4>
                        <p className="text-sm text-[#999999]">Formal and business-appropriate</p>
                      </div>
                      <div className="text-xs bg-[#333333] text-[#999999] px-2 py-1 rounded">
                        Built-in
                      </div>
                    </div>
                  </button>

                  {/* Friendly Template */}
                  <button
                    onClick={async () => {
                      setSelectedEmailStyle('friendly')
                      await handleEmailStyleChange('friendly')
                      setShowEmailComposer({
                        ...showEmailComposer,
                        content: generateEmailContent('friendly')
                      })
                    }}
                    className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                      selectedEmailStyle === 'friendly'
                        ? 'border-[#FFB27D] bg-[#FFB27D]/10'
                        : 'border-[#333333] hover:border-[#555555]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-white">Friendly</h4>
                        <p className="text-sm text-[#999999]">Casual and approachable</p>
                      </div>
                      <div className="text-xs bg-[#333333] text-[#999999] px-2 py-1 rounded">
                        Built-in
                      </div>
                    </div>
                  </button>

                  {/* Custom Templates */}
                  {emailTemplates.map((template) => (
                    <button
                      key={template.id}
                      onClick={async () => {
                        const customStyle = `custom-${template.id}`
                        setSelectedEmailStyle(customStyle)
                        await handleEmailStyleChange(customStyle)
                        setShowEmailComposer({
                          ...showEmailComposer,
                          content: generateEmailContent(customStyle)
                        })
                      }}
                      className={`w-full p-4 rounded-lg border-2 transition-colors text-left ${
                        selectedEmailStyle === `custom-${template.id}`
                          ? 'border-[#FFB27D] bg-[#FFB27D]/10'
                          : 'border-[#333333] hover:border-[#555555]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-white">{template.name}</h4>
                          <p className="text-sm text-[#999999]">{template.description || 'Custom template'}</p>
                          {template.usageCount > 0 && (
                            <p className="text-xs text-[#666666] mt-1">
                              Used {template.usageCount} times
                            </p>
                          )}
                        </div>
                        <div className="text-xs bg-[#8B5CF6] text-white px-2 py-1 rounded">
                          Custom
                        </div>
                      </div>
                    </button>
                  ))}

                  {/* Loading state */}
                  {isLoadingTemplates && (
                    <div className="text-center py-4 text-[#666666]">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#FFB27D] mx-auto"></div>
                      <p className="mt-2">Loading templates...</p>
                    </div>
                  )}

                  {/* Manage Templates Button */}
                  <button
                    onClick={() => setShowTemplateManager(true)}
                    className="w-full p-3 rounded-lg border-2 border-dashed border-[#333333] hover:border-[#555555] transition-colors text-center"
                  >
                    <div className="flex items-center justify-center gap-2 text-[#999999]">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      <span>Manage Templates</span>
                    </div>
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Email Composer Modal */}
        {showEmailComposer && typeof showEmailComposer === 'object' && showEmailComposer.content && (
          <ComposeEmail
            isOpen={true}
            onClose={() => setShowEmailComposer(false)}
            user={auth.currentUser}
            replyToEmail={showEmailComposer}
            activeAccountId={activeAccountId}
            onEmailSent={async () => {
              setShowEmailComposer(false)
              setSentToEmail(agreement.clientEmail)
              setShowSentSuccess(true)
              
              // Update agreement status in Firestore
              try {
                const db = getFirestore()
                await updateDoc(doc(db, 'agreements', agreement.id), {
                  status: 'sent',
                  sentAt: new Date().toISOString(),
                  sentTo: agreement.clientEmail
                })
                // Update local state
                setAgreement(prev => ({
                  ...prev,
                  status: 'sent',
                  sentAt: new Date().toISOString(),
                  sentTo: agreement.clientEmail
                }))
              } catch (error) {
                console.error('Error updating agreement status:', error)
              }
            }}
          />
        )}

        {/* Email Template Manager Modal */}
        {showTemplateManager && (
          <EmailTemplateManager
            isOpen={showTemplateManager}
            onClose={() => setShowTemplateManager(false)}
            onTemplateUpdated={async () => {
              if (emailTemplateService) {
                const templates = await emailTemplateService.getTemplates()
                setEmailTemplates(templates)
              }
            }}
          />
        )}

        {/* Success Modal - Agreement Sent */}
        {showSentSuccess && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#1A1A1A] rounded-2xl shadow-2xl w-full max-w-md border border-[#333333] overflow-hidden"
            >
              {/* Success Icon */}
              <div className="bg-gradient-to-br from-[#FFB27D]/20 to-[#FF8F3F]/10 p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="w-20 h-20 mx-auto bg-[#FFB27D] rounded-full flex items-center justify-center"
                >
                  <svg className="w-10 h-10 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
              </div>

              {/* Content */}
              <div className="p-6 text-center">
                <h3 className="text-2xl font-semibold text-white mb-2">Agreement Sent!</h3>
                <p className="text-[#999999] mb-6">
                  Your agreement has been sent successfully to:
                </p>
                
                {/* Recipient Info */}
                <div className="bg-black/50 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FFB27D] to-[#FF8F3F] flex items-center justify-center text-black font-medium">
                      {(agreement?.clientName || sentToEmail || 'C').charAt(0).toUpperCase()}
                    </div>
                    <div className="text-left">
                      <p className="text-white font-medium">{agreement?.clientName || 'Client'}</p>
                      <p className="text-sm text-[#666666]">{sentToEmail}</p>
                    </div>
                  </div>
                </div>

                {/* What's Next */}
                <div className="text-left bg-[#0A0A0A] rounded-xl p-4 mb-6">
                  <p className="text-xs uppercase tracking-wider text-[#666666] mb-3">What happens next</p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#FFB27D]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-[#FFB27D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <p className="text-sm text-[#999999]">Client receives the agreement via email</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#FFB27D]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-[#FFB27D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </div>
                      <p className="text-sm text-[#999999]">They review and sign electronically</p>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#FFB27D]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-[#FFB27D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                      </div>
                      <p className="text-sm text-[#999999]">You'll be notified when they sign</p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowSentSuccess(false)}
                    className="flex-1 py-3 px-4 bg-[#2A2A2A] hover:bg-[#333333] text-white rounded-xl font-medium transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowSentSuccess(false)
                      navigate('/app/agreements')
                    }}
                    className="flex-1 py-3 px-4 bg-[#FFB27D] hover:bg-[#FFA164] text-black rounded-xl font-medium transition-colors"
                  >
                    View All Agreements
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    )
  } catch (error) {
    console.error('Error rendering Agreement:', error);
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#1A1A1A] rounded-lg p-8 text-center">
          <h1 className="text-2xl font-medium text-[#FFB27D] mb-4">Something Went Wrong</h1>
          <p className="text-[#999999] mb-6">
            We encountered an error while displaying this agreement.
          </p>
          <div className="space-y-4">
            <button
              onClick={() => navigate(-1)}
              className="w-full py-2 px-4 bg-[#FFB27D] text-black rounded-lg hover:bg-[#FFA164] transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full py-2 px-4 bg-[#1A1A1A] text-[#FFB27D] border border-[#FFB27D] rounded-lg hover:bg-[#FFB27D] hover:text-black transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}